/**
 * 全屏 Markdown 编辑器 + 实时预览
 * 用于知识库文件编辑，支持 Mermaid 图表、KaTeX 数学公式
 * 左侧：Markdown 源码编辑 · 右侧：MarkdownRenderer 实时预览
 * 支持：工具栏、上传图片、从图库选择图片
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import {
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

interface ImageItem {
  url: string;
  page?: number | null;
  index?: number;
  description?: string;
  file_name?: string;
  file_id?: number;
}

interface RichTextEditorProps {
  /** 当前 Markdown 内容 */
  value: string;
  /** 内容变更回调 */
  onChange: (markdown: string) => void;
  /** 图库图片列表 */
  imageGallery?: ImageItem[];
  /** 上传图片回调，返回上传后的 URL */
  onImageUpload?: (file: File) => Promise<string>;
  /** 占位文本 */
  placeholder?: string;
  /** 将相对路径转为绝对 URL */
  resolveUrl?: (url: string) => string;
  /** 标题 */
  title?: string;
  /** 保存回调 */
  onSave?: () => void;
  /** 关闭回调 */
  onClose?: () => void;
}

/** 快速插入工具栏按钮 */
interface ToolBtn {
  label: string;
  icon: string;
  insert: string;
  block?: boolean;
}

const TOOLBAR_BUTTONS: ToolBtn[] = [
  { label: '标题1', icon: 'H1', insert: '# ', block: true },
  { label: '标题2', icon: 'H2', insert: '## ', block: true },
  { label: '标题3', icon: 'H3', insert: '### ', block: true },
  { label: '粗体', icon: 'B', insert: '****', block: false },
  { label: '斜体', icon: 'I', insert: '**', block: false },
  { label: '列表', icon: '•', insert: '- ', block: true },
  { label: '编号', icon: '1.', insert: '1. ', block: true },
  { label: '表格', icon: '⊞', insert: '\n| 列1 | 列2 | 列3 |\n| --- | --- | --- |\n| 内容 | 内容 | 内容 |\n', block: true },
  { label: '代码', icon: '<>', insert: '\n```\n\n```\n', block: true },
  { label: '公式', icon: '𝑓', insert: '\n$$\n\n$$\n', block: true },
  { label: 'Mermaid', icon: '📊', insert: '\n```mermaid\ngraph TD\n  A[开始] --> B[结束]\n```\n', block: true },
  { label: '引用', icon: '❝', insert: '> ', block: true },
  { label: '分割线', icon: '—', insert: '\n---\n', block: true },
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  imageGallery,
  onImageUpload,
  placeholder = '在此输入 Markdown 内容...',
  resolveUrl,
  title,
  onSave,
  onClose,
}) => {
  const [content, setContent] = useState(value);
  const [previewContent, setPreviewContent] = useState(value);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [showPreviewOnly, setShowPreviewOnly] = useState(false);
  const [syncScroll, setSyncScroll] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSyncingRef = useRef(false);

  // 安全处理 URL
  const safeUrl = useCallback(
    (url: string) => resolveUrl ? resolveUrl(url) : url,
    [resolveUrl],
  );

  // 同步外部 value 变化
  useEffect(() => {
    if (value !== content) {
      setContent(value);
      setPreviewContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 防抖更新预览（500ms）
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewContent(content);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [content]);

  // ESCC 关闭 · Ctrl/Cmd+S 保存
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, onSave, onClose]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // 内容变更 → 回调
  const handleChange = useCallback(
    (newContent: string) => {
      setContent(newContent);
      onChange(newContent);
    },
    [onChange],
  );

  // 左→右 滚动同步
  const handleEditorScroll = useCallback(() => {
    if (!syncScroll || isSyncingRef.current || !editorRef.current || !previewRef.current) return;
    isSyncingRef.current = true;
    const editor = editorRef.current;
    const preview = previewRef.current;
    const ratio = editor.scrollTop / (editor.scrollHeight - editor.clientHeight);
    if (editor.scrollHeight > editor.clientHeight) {
      preview.scrollTop = ratio * (preview.scrollHeight - preview.clientHeight);
    }
    requestAnimationFrame(() => { isSyncingRef.current = false; });
  }, [syncScroll]);

  // 右→左 滚动同步
  const handlePreviewScroll = useCallback(() => {
    if (!syncScroll || isSyncingRef.current || !previewRef.current || !editorRef.current) return;
    isSyncingRef.current = true;
    const preview = previewRef.current;
    const editor = editorRef.current;
    const ratio = preview.scrollTop / (preview.scrollHeight - preview.clientHeight);
    if (preview.scrollHeight > preview.clientHeight) {
      editor.scrollTop = ratio * (editor.scrollHeight - editor.clientHeight);
    }
    requestAnimationFrame(() => { isSyncingRef.current = false; });
  }, [syncScroll]);

  // 拖拽分隔线
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      setSplitRatio(Math.max(20, Math.min(80, (x / rect.width) * 100)));
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // 插入文本到光标位置
  const insertAtCursor = useCallback(
    (insertText: string, isBlock: boolean = false) => {
      const textarea = editorRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = content.substring(0, start);
      const selected = content.substring(start, end);
      const after = content.substring(end);

      let newText: string;
      let cursorPos: number;

      if (isBlock) {
        const lineStart = before.lastIndexOf('\n') + 1;
        const beforeLine = before.substring(0, lineStart);
        const afterLine = before.substring(lineStart);
        newText = beforeLine + insertText + afterLine + selected + after;
        cursorPos = beforeLine.length + insertText.length;
      } else if (insertText.includes('**')) {
        const wrapper = insertText.replace('**', '');
        if (selected) {
          newText = before + wrapper + selected + wrapper + after;
          cursorPos = before.length + wrapper.length + selected.length + wrapper.length;
        } else {
          newText = before + insertText + after;
          cursorPos = before.length + insertText.length - 2;
        }
      } else {
        newText = before + insertText + selected + after;
        cursorPos = before.length + insertText.length + selected.length;
      }

      handleChange(newText);
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          editorRef.current.selectionStart = cursorPos;
          editorRef.current.selectionEnd = cursorPos;
        }
      }, 0);
    },
    [content, handleChange],
  );

  // 插入图片 Markdown 语法
  const insertImage = useCallback(
    (url: string, alt: string = '图片') => {
      insertAtCursor(`![${alt}](${url})`, false);
    },
    [insertAtCursor],
  );

  // 上传图片
  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/bmp'];
      if (!allowedTypes.includes(file.type)) {
        alert('不支持的图片格式，支持: PNG, JPEG, GIF, WebP, BMP');
        return;
      }
      if (!onImageUpload) {
        alert('图片上传功能不可用');
        return;
      }

      setUploading(true);
      try {
        const uploadedUrl = await onImageUpload(file);
        insertImage(uploadedUrl, file.name);
      } catch (err) {
        console.error('图片上传失败:', err);
        alert('图片上传失败，请重试');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [onImageUpload, insertImage],
  );

  // 从图库选择图片
  const handleGallerySelect = useCallback(
    (img: ImageItem) => {
      const url = safeUrl(img.url);
      insertImage(url, img.description || img.file_name || '图片');
      setShowGallery(false);
    },
    [safeUrl, insertImage],
  );

  // 保存
  const handleSave = useCallback(() => {
    onSave?.();
    setSaveStatus('saved');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, [onSave]);

  // Tab 键处理
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      handleChange(content.substring(0, start) + '  ' + content.substring(end));
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  const allGalleryImages = imageGallery || [];

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-gray-900">
      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between bg-gray-800 border-b border-gray-700 px-4 py-2 flex-shrink-0 select-none">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-medium text-gray-200">
            {title ? `编辑：${title}` : '编辑内容'}
          </h2>
          <span className="text-xs text-gray-400">Ctrl+S 保存 · Esc 关闭</span>
          <span className="px-2 py-0.5 text-xs rounded bg-gray-700 text-blue-400 font-mono">
            当前字符数：{content.length}字
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* 编辑/预览切换 */}
          <div className="flex items-center bg-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setShowPreviewOnly(false)}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${
                !showPreviewOnly ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
              title="分屏编辑"
            >
              <PencilIcon className="w-3.5 h-3.5" />
              <span>编辑</span>
            </button>
            <button
              onClick={() => setShowPreviewOnly(true)}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${
                showPreviewOnly ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
              title="仅预览"
            >
              <EyeIcon className="w-3.5 h-3.5" />
              <span>预览</span>
            </button>
          </div>

          <div className="w-px h-5 bg-gray-600 mx-1" />

          {/* 同步滚动 */}
          {!showPreviewOnly && (
            <label className="flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer select-none"
              title={syncScroll ? '左右跟随滚动：是' : '左右跟随滚动：否'}>
              <input
                type="checkbox"
                checked={syncScroll}
                onChange={(e) => setSyncScroll(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-500 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className={syncScroll ? 'text-gray-200' : 'text-gray-500'}>同步滚动</span>
            </label>
          )}

          <div className="w-px h-5 bg-gray-600 mx-1" />

          <button
            onClick={handleSave}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            保存 (Ctrl+S)
          </button>
          <button
            onClick={() => onClose?.()}
            className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title="关闭 (Esc)"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 快速插入工具栏 */}
      {!showPreviewOnly && (
        <div className="flex items-center gap-0.5 border-b border-gray-700 px-3 py-1.5 overflow-x-auto flex-shrink-0 select-none"
          style={{ backgroundColor: '#1f2937' }}>
          {TOOLBAR_BUTTONS.map((btn) => (
            <button
              key={btn.label}
              onClick={() => insertAtCursor(btn.insert, btn.block)}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors whitespace-nowrap"
              title={btn.label}
            >
              {btn.icon}
            </button>
          ))}
          {/* 分隔 */}
          <span className="w-px h-4 bg-gray-600 mx-1" />

          {/* 上传图片 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/bmp"
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !onImageUpload}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors whitespace-nowrap disabled:opacity-50"
            title="上传图片"
          >
            {uploading ? (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                上传中
              </span>
            ) : (
              <>
                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                上传
              </>
            )}
          </button>

          {/* 图库 */}
          <button
            onClick={() => setShowGallery(!showGallery)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors whitespace-nowrap ${
              showGallery
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-600'
            }`}
            title="从图库选择"
          >
            <PhotoIcon className="w-3.5 h-3.5" />
            图库
          </button>
        </div>
      )}

      {/* 图库选择面板 */}
      {showGallery && !showPreviewOnly && (
        <div className="border-b border-gray-700 bg-gray-800 px-3 py-2 flex-shrink-0 select-none">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">
              点击缩略图插入 · 共 {allGalleryImages.length} 张
            </span>
            <button onClick={() => setShowGallery(false)} className="text-xs text-gray-500 hover:text-gray-300">
              关闭
            </button>
          </div>
          {allGalleryImages.length === 0 ? (
            <div className="text-xs text-gray-500 py-3 text-center">暂无图片，请先上传</div>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
              {allGalleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => handleGallerySelect(img)}
                  className="cursor-pointer border-2 border-gray-600 rounded overflow-hidden hover:border-blue-400 transition-colors flex-shrink-0 group relative"
                  title={img.description || img.file_name || `图片 ${idx + 1}`}
                >
                  <img
                    src={safeUrl(img.url)}
                    alt={img.description || `图片 ${idx + 1}`}
                    className="w-14 h-14 object-cover"
                    loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 主编辑区域 */}
      <div
        ref={containerRef}
        className={`flex-1 flex min-h-0 ${isDragging ? 'cursor-col-resize select-none' : ''}`}
      >
        {/* 左侧：Markdown 编辑区 */}
        {!showPreviewOnly && (
          <>
            <div style={{ width: `${splitRatio}%` }} className="flex flex-col min-h-0">
              <textarea
                ref={editorRef}
                value={content}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onScroll={handleEditorScroll}
                className="flex-1 bg-gray-900 text-gray-100 p-4 font-mono text-sm leading-relaxed resize-none outline-none border-r border-gray-700"
                placeholder={placeholder}
                spellCheck={false}
              />
            </div>
            {/* 可拖拽分隔线 */}
            <div
              onMouseDown={handleDragStart}
              className="w-1.5 bg-gray-700 hover:bg-blue-500 cursor-col-resize flex-shrink-0 transition-colors relative group"
            >
              <div className="absolute inset-y-0 -left-1 -right-1" />
            </div>
          </>
        )}

        {/* 右侧：实时预览（MarkdownRenderer: 支持 Mermaid + KaTeX） */}
        <div
          ref={previewRef}
          onScroll={handlePreviewScroll}
          className="flex-1 min-w-0 overflow-y-auto bg-white"
          style={showPreviewOnly ? {} : { width: `${100 - splitRatio}%` }}
        >
          <div className="p-6 max-w-4xl mx-auto">
            {previewContent.trim() ? (
              <MarkdownRenderer
                content={previewContent}
                mermaidDefaultView="chart"
                enableMermaid={true}
                enableMath={true}
              />
            ) : (
              <div className="flex items-center justify-center h-full min-h-[200px] text-gray-400 text-sm">
                {showPreviewOnly ? '暂无内容' : '在左侧输入内容，这里将实时预览...'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部状态栏 */}
      <div className="flex items-center justify-between bg-gray-800 border-t border-gray-700 px-4 py-1 flex-shrink-0 select-none">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>Markdown</span>
          <span>{content.length} 字符</span>
          <span>{content.split('\n').length} 行</span>
          {saveStatus !== 'idle' && (
            <span className="text-green-400">✓ 已保存</span>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {!showPreviewOnly && (
            <span>编辑 {splitRatio.toFixed(0)}% : 预览 {(100 - splitRatio).toFixed(0)}%</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
